import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import { confirmDialog } from '../confirm';
import { useDismiss, useEscapeToClose, useFocusTrap } from '../motion';
import Icon from './Icon';
import { parseCsv, valuesIn, looksBinary, type Sheet } from '../import-csv';
import { parseXlsx, looksLikeExcel } from '../import-xlsx';
import { guessDateOrder, parseImportDate, type DateOrder } from '../when';
import { asDayMonth } from '../utils';
import {
  guessColumns, guessValue, guessKind, suggestBands, isNumeric, buildImport,
  schemeFrom, refFor, assignRefs, pickScheme, defaultChoice, type NumberChoice, FIELD_LABEL, BLOCKED_WORDS,
  type Destination, type Field, type Sureness, type Scheme,
} from '../import-plan';
import { liveTypes } from '../issue-types';
import type { Issue, IssueStatus, IssueKind } from '../types';

/**
 * Bringing a backlog in from a spreadsheet.
 *
 * WHY THIS EXISTS. TRINRE keep their work in Excel — some of it exported from
 * an MS Loop board — and until now the only way into FlightDeck was typing it
 * again. A customer whose backlog is already written should be able to bring it
 * with them.
 *
 * THE ONE RULE IT IS BUILT AROUND, Vinnie 19 August 2026:
 *
 *     "We never drop them, we ask the user to map them."
 *
 * So nothing here refuses a file for having the wrong shape and nothing is
 * discarded in silence. A column FlightDeck has no field for is kept in the
 * description under its own heading; a word FlightDeck does not recognise is a
 * question. The screen IS the mapping — that was the whole point of the option
 * chosen on 19 August.
 *
 * WHY THREE STEPS AND NOT ONE. Columns and words are different questions and
 * mixing them makes both harder: "the column called Status is the status" is
 * about the file's shape, "Completed Development means In review" is about
 * somebody's vocabulary. The third step exists because an import is not
 * undoable — 78 cards is not something to find out about afterwards.
 *
 * IMPORT ONCE, NOT SYNC. Vinnie settled that on 19 August: this is somebody
 * moving off a manual system, not a link between two systems that both keep
 * running. Nothing here writes back to the file.
 */

const STEPS = ['The columns', 'The words', 'Check it'];

/** The fields whose contents are a fixed set of words, so their values need mapping too. */
const CHOICES: Partial<Record<Field, [string, string][]>> = {
  status: [['backlog', 'Backlog'], ['todo', 'To do'], ['inprogress', 'In progress'], ['inreview', 'In review'], ['done', 'Done'], ['wontdo', "Won't do"]],
  priority: [['critical', 'Critical'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']],
  kind: [['task', 'Task'], ['bug', 'Bug']],
  size: [['quick', 'Quick'], ['medium', 'A few days'], ['long', 'Long']],
};

const DESTINATIONS: [Destination, string][] = [
  ['note', 'Keep it in the description'],
  ...(Object.entries(FIELD_LABEL) as [Field, string][]).map(([f, l]) => [f, l] as [Destination, string]),
  ['skip', 'Leave it out'],
];

const SURE_NOTE: Record<Sureness, string> = {
  exact: 'the same word',
  guess: 'our reading of it — worth a look',
  '': 'not a word FlightDeck knows',
};

export default function Import({ close }: { close: () => void }) {
  const { leaving, dismiss } = useDismiss(close);
  const importIssues = useApp((s) => s.importIssues);
  const meta = useApp((s) => s.meta);
  const onBoard = useApp((s) => s.issues);
  /*
   * THIS BOARD'S OWN TYPES — what an imported card's kind is checked against.
   * A card cannot carry a type the board does not have (the foreign key that
   * imports used to fail on), and since #302 no board has 'task' by default, so
   * the importer can no longer assume any fixed set. Retired types are left out:
   * nothing new should arrive already wearing one.
   */
  const issueTypes = useApp((s) => s.issueTypes);
  const boardTypes = useMemo(
    () => liveTypes(issueTypes).map((t) => ({ slug: t.slug, name: t.name })),
    [issueTypes],
  );
  /** The board's types as [value, label] pairs, for the type dropdowns. */
  const kindChoices = useMemo(() => boardTypes.map((t) => [t.slug, t.name] as [string, string]), [boardTypes]);
  /* In Staging is a landing column only on a board that turned it on — so the
     import offers it as a status, and labels it, only there. On every other board
     the choices are the six originals, and an auto-detected "in staging" value is
     sent back to the person to map (see buildImport's stagingEnabled guard). */
  const stagingOn = meta.stagingEnabled === true;
  const statusChoices = useMemo<[string, string][]>(() => {
    const base = CHOICES.status!;
    if (!stagingOn) return base;
    const at = base.findIndex(([v]) => v === 'inreview');
    const withStaging = [...base];
    withStaging.splice(at < 0 ? base.length : at, 0, ['staging', 'In staging']);
    return withStaging;
  }, [stagingOn]);

  const [name, setName] = useState('');
  const [sheet, setSheet] = useState<Sheet | null>(null);
  // Kept only for a workbook, so a different tab can be read without the file
  // being chosen again. Null for a CSV, which has nothing to switch between.
  const [book, setBook] = useState<ArrayBuffer | null>(null);
  const [problem, setProblem] = useState('');
  const [step, setStep] = useState(0);
  const [columns, setColumns] = useState<Destination[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [bands, setBands] = useState({ quickUpTo: 1, longFrom: 3 });
  const [blockedStatus, setBlockedStatus] = useState<IssueStatus | ''>('');
  // What a row with no type becomes, chosen from this board's types. No default,
  // for the reason on Plan.defaultKind: there is no honest kind to assume.
  const [defaultKind, setDefaultKind] = useState<IssueKind | ''>('');
  const [choice, setChoice] = useState<NumberChoice>('keep');
  // Which way this file's numeric dates read — guessed from the date column when
  // the file is read, overridable on the last step. Day-first is the default.
  const [dateOrder, setDateOrder] = useState<DateOrder>('dmy');
  const [busy, setBusy] = useState(false);
  const [landed, setLanded] = useState(0);
  const [done, setDone] = useState<{ made: number; failed: number; why: string } | null>(null);

  /*
   * The columns, words and bands to guess from a freshly read sheet. Shared by
   * the first read and by switching to another tab of a workbook, so both land
   * on step 1 with everything guessable already guessed.
   */
  const applySheet = (s: Sheet, fileName: string) => {
    const guessed = guessColumns(s.headers);
    const cols = guessed.map((g) => g.to);
    const sizeAt = cols.indexOf('size');
    setName(fileName);
    setSheet(s);
    setColumns(cols);
    setValues({});
    setBands(sizeAt >= 0 ? suggestBands(s.rows.map((r) => r[sizeAt])) : { quickUpTo: 1, longFrom: 3 });
    // A file's dates read one way throughout, so the order is guessed once from
    // whichever date column it has — a value over twelve settles it, otherwise
    // day-first — and the person confirms it on the last step. #423
    const dateAt = cols.indexOf('due') >= 0 ? cols.indexOf('due') : cols.indexOf('start');
    setDateOrder(dateAt >= 0 ? guessDateOrder(valuesIn(s, dateAt).map((v) => v.value)).order : 'dmy');
    setStep(0);
  };

  /** Read the file, guess everything guessable, and show the first question. */
  const take = async (file: File) => {
    setProblem('');
    try {
      // An Excel workbook is read directly now — no save-as-CSV first (#368).
      // It is binary by nature, so it never goes near looksBinary.
      if (looksLikeExcel(file)) {
        const buf = await file.arrayBuffer();
        const s = await parseXlsx(buf);
        if (!s.headers.length) return setProblem(`${file.name} has no rows we could read — its sheets look empty.`);
        if (!s.rows.length) return setProblem('That sheet has headings but no rows under them.');
        setBook(buf);
        applySheet(s, file.name);
        return;
      }
      const text = await file.text();
      // Said before anything is parsed, so the complaint is about the real
      // problem rather than about a title column in a file of binary.
      if (looksBinary(text)) {
        return setProblem(
          `${file.name} does not look like a spreadsheet — it looks like a document, an image, or`
          + ` another kind of file. Choose a CSV, or an Excel (.xlsx) file, exported from your`
          + ` spreadsheet.`
        );
      }
      const s = parseCsv(text);
      if (!s.headers.length) return setProblem('That file has nothing in it.');
      if (!s.rows.length) return setProblem('That file has headings but no rows under them.');
      setBook(null);
      applySheet(s, file.name);
    } catch (e) {
      setProblem(`That file could not be read — ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  /** Read another tab of the same workbook, re-guessing for its columns. */
  const switchSheet = async (nm: string) => {
    if (!book) return;
    setProblem('');
    try {
      const s = await parseXlsx(book, nm);
      applySheet(s, name);
    } catch (e) {
      setProblem(`That sheet could not be read — ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  /* Everything downstream of the answers so far, recomputed as they change. */
  const plan = useMemo(
    () => ({ columns, values, bands, blockedStatus, defaultKind, dateOrder }),
    [columns, values, bands, blockedStatus, defaultKind, dateOrder],
  );

  const words = useMemo(() => {
    if (!sheet) return { asks: [], reading: [], exact: [], numeric: [] as { field: Field; column: number }[] };
    const asks: WordQ[] = []; const reading: WordQ[] = []; const exact: WordQ[] = [];
    const numeric: { field: Field; column: number }[] = [];
    columns.forEach((d, column) => {
      const field = d as Field;
      if (!CHOICES[field]) return;
      const vals = valuesIn(sheet, column);
      if (isNumeric(vals.map((v) => v.value))) { numeric.push({ field, column }); return; }
      for (const v of vals) {
        /*
         * "Blocked" HAS ITS OWN QUESTION and must not also appear in this list.
         * Caught by walking the built screen: it showed up twice, once here and
         * once in the block above it — two controls for one decision, free to
         * disagree, with the second silently winning.
         */
        if (field === 'status' && BLOCKED_WORDS.includes(v.value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim())) continue;
        // Kind is checked against the board's own types, not a fixed list — see
        // guessKind. Everything else keeps its fixed vocabulary.
        const g = field === 'kind' ? guessKind(v.value, boardTypes) : guessValue(field, v.value);
        const q: WordQ = { field, column, value: v.value, rows: v.rows, guess: g.to, sure: g.sure };
        (g.sure === 'exact' ? exact : g.sure === 'guess' ? reading : asks).push(q);
      }
    });
    return { asks, reading, exact, numeric };
  }, [sheet, columns, boardTypes]);

  const built = useMemo(() => (sheet ? buildImport(sheet, plan, boardTypes, stagingOn) : null), [sheet, plan, boardTypes, stagingOn]);

  /*
   * THEIR NUMBERING, IF THEY HAVE ONE — Vinnie's decision of 19 August: their
   * numbers are what signed documents point at, so they come with the work.
   */
  const theirScheme = useMemo(() => {
    if (!sheet) return null;
    const at = columns.indexOf('reference');
    return at < 0 ? null : schemeFrom(sheet.rows.map((r) => r[at]));
  }, [sheet, columns]);

  // A scheme the BOARD already carries, from something imported here before. A
  // sheet with no numbers of its own must NOT silently inherit it — that once
  // turned every later import on an LH-imported board into LH with no say in it
  // (Vinnie, 10 Sep). So it is offered below, never assumed.
  const boardScheme: Scheme | null = useMemo(
    () =>
      meta.refPrefix && meta.refPrefix !== '#'
        ? { prefix: meta.refPrefix, width: meta.refWidth ?? 0, next: meta.nextNumber || 1 }
        : null,
    [meta.refPrefix, meta.refWidth, meta.nextNumber],
  );

  // The sheet brought its own prefix AND the board already has a different one —
  // the transfer-onto-an-existing-board case the person must reconcile, not have
  // mixed in unnoticed (Vinnie, 11 Sep).
  const differs =
    !!theirScheme && !!boardScheme &&
    theirScheme.prefix.toUpperCase() !== boardScheme.prefix.toUpperCase();

  // House numbering starts past every #-card already here, so it never lands on
  // one that exists — assignRefs de-duplicates within a batch, not against the
  // board, and meta.nextNumber counts the board's OTHER scheme when it has one.
  const houseNext = useMemo(() => {
    let n = boardScheme ? 1 : meta.nextNumber || 1;
    for (const i of onBoard) {
      const m = /^#(\d+)$/.exec(i.ref);
      if (m) n = Math.max(n, Number(m[1]) + 1);
    }
    return n;
  }, [onBoard, meta.nextNumber, boardScheme]);
  const houseScheme: Scheme = { prefix: '#', width: 0, next: houseNext };

  // Where the numbering choice starts — the sheet's own, else the board's (the
  // board wins for a numberless sheet), else house. Only a starting point; the
  // person verifies it below. See defaultChoice.
  useEffect(() => {
    setChoice(defaultChoice(theirScheme, boardScheme));
  }, [theirScheme, boardScheme]);

  const { scheme, useTheirs: usingTheirs } = pickScheme({
    sheet: theirScheme,
    board: boardScheme,
    house: houseScheme,
    choice,
  });
  const numbered = useMemo(
    () => (built ? assignRefs(built.drafts, scheme, usingTheirs) : null),
    [built, scheme.prefix, scheme.width, scheme.next, usingTheirs],
  );

  /*
   * NUMBERS THIS BOARD ALREADY HAS.
   *
   * The database enforces one reference per card per board, so importing a file
   * whose numbers are already here is refused row by row — and Vinnie found out
   * about it at step 5.14 only AFTER watching a progress bar fill to the end.
   * A check that could have run before the first write should not be delivered
   * as a post-mortem. Worked out here so step 3 can say it in advance.
   */
  const taken = useMemo(() => new Set(onBoard.map((i) => i.ref.toUpperCase())), [onBoard]);
  const clashing = useMemo(
    () => (numbered ? numbered.refs.filter((r) => taken.has(r.toUpperCase())) : []),
    [numbered, taken],
  );

  const titleSet = columns.includes('title');
  const stillAsking = words.asks.filter((q) => !values[`${q.field}:${q.value}`]).length
    + (built?.problems.some((p) => p.kind === 'blocked-unanswered') && !blockedStatus ? 1 : 0)
    + (built?.problems.some((p) => p.kind === 'untyped-unanswered') && !defaultKind ? 1 : 0);

  const run = async () => {
    if (!sheet || !built || !numbered) return;
    setBusy(true);
    setLanded(0);
    const rows = built.drafts.map((d, i) => ({
      draft: {
        ref: numbered.refs[i],
        kind: d.kind,
        title: d.title,
        description: d.description,
        status: d.status,
        priority: d.priority,
        assignee: d.assignee ?? '',
        ...(d.responsible ? { responsible: d.responsible } : {}),
        labels: d.labels ?? [],
        ...(d.size ? { size: d.size } : {}),
        ...(d.epic ? { epic: d.epic } : {}),
        ...(d.dueDate ? { dueDate: d.dueDate } : {}),
        ...(d.startsOn ? { startsOn: d.startsOn } : {}),
        ...(d.blocked ? { blocked: d.blocked } : {}),
        ...(d.blockerTarget ? { blockerTarget: d.blockerTarget } : {}),
        // What the history will say about how this card got here — see
        // Issue.importedFrom and migration 0032.
        importedFrom: name,
      } as Omit<Issue, 'id' | 'createdAt' | 'updatedAt' | 'comments'>,
    }));
    const out = await importIssues(
      rows,
      // The scheme the board carries on with — advanced past whatever was just
      // assigned. house or carried, numbered.scheme is the authority.
      numbered.scheme,
      (done) => setLanded(done),
    );
    setBusy(false);
    setDone(out);
  };

  /*
   * CLOSING IT PART-WAY THROUGH SAYS WHAT CLOSING IT COSTS.
   *
   * Asked for by Vinnie on 20 August 2026. By step 2 somebody may have answered
   * a dozen questions about their own vocabulary, and none of it is written
   * down anywhere until Import is pressed — so the cross in the corner quietly
   * throws all of it away. It only asks when there is something to lose: not
   * before a file is chosen, not on the screen that says it is finished, and
   * never while it is actually writing.
   *
   * ⚠️ DEFINED, AND THE ESCAPE HOOK CALLED, ABOVE THE EARLY RETURNS BELOW —
   * a hook after `if (!sheet) return` runs on some renders and not others, which
   * is the "rendered more hooks than during the previous render" crash the
   * moment a file is chosen. Every render must reach this line.
   */
  const leave = async () => {
    const ok = await confirmDialog({
      title: 'Close without importing?',
      body: `The ${built?.drafts.length ?? 0} cards stay unimported, and the answers you have given about your own columns and wording are lost. Nothing on the board changes.`,
      confirmLabel: 'Close',
      cancelLabel: 'Keep going',
      tone: 'caution',
    });
    if (!ok) return;
    dismiss();
  };
  // Escape leaves the same way the close cross does — through the same warning —
  // and is held back while an import is running, exactly as the cross is.
  useEscapeToClose(leave, !busy);

  /* ── The file has not been chosen yet ──────────────────────────────────── */
  if (!sheet) {
    return (
      <Shell leaving={leaving} dismiss={dismiss} step={-1}>
        <p className="hint feedback-intro">
          Choose your spreadsheet — an Excel file (.xlsx) or a CSV. Nothing is imported until you
          have seen what it will do.
        </p>
        <label className="stack">
          <span className="feedback-q">Choose the file</span>
          <input
            className="input"
            type="file"
            accept=".xlsx,.xls,.csv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,text/plain"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void take(f); }}
          />
        </label>
        {problem && <p className="feedback-problem" role="alert">{problem}</p>}
        <div className="feedback-foot">
          <span className="hint">It is read here on your machine. Nothing is sent anywhere until you press Import.</span>
          <button className="btn" onClick={dismiss}>Cancel</button>
        </div>
      </Shell>
    );
  }

  /* ── It finished ───────────────────────────────────────────────────────── */
  if (done) {
    return (
      <Shell leaving={leaving} dismiss={dismiss} step={-1}>
        <h3 style={{ margin: 0, color: 'var(--t1)' }}>
          {done.failed === 0 ? `${done.made} cards are on the board.` : `${done.made} in, ${done.failed} refused.`}
        </h3>
        {done.failed > 0 && <p className="feedback-problem" role="alert">{done.why}</p>}
        {choice === 'keep' && theirScheme && done.made > 0 && (
          <p className="hint">
            This board now numbers cards the way your sheet does. The next one raised here will be{' '}
            <b>{refFor(numbered!.scheme, numbered!.scheme.next)}</b>.
          </p>
        )}
        {/* Only when something actually landed. On a run the database refused
            outright it was still announcing that three cards had been given new
            numbers — a report about cards that do not exist. */}
        {done.made > 0 && choice === 'keep' && numbered && numbered.renumbered.length > 0 && (
          <>
            <div className="grp-heading">Given a new number · {numbered.renumbered.length}</div>
            <p className="hint" style={{ margin: 0 }}>
              Your sheet used these more than once, and two cards cannot share one reference. Worth
              noting if a signed document points at them.
            </p>
            <div className="grp-list is-contents" style={{ maxHeight: 'none' }}>
              {numbered.renumbered.map((r) => (
                <div key={r.now} className="grp-row is-work import-row is-wide" >
                  <div className="work-main" style={{ minWidth: 0 }}>
                    <div style={{ color: 'var(--t1)' }}>{r.was} is now <b>{r.now}</b></div>
                    <div className="hint" style={{ fontSize: 12 }}>{r.title}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="feedback-foot">
          <span className="hint" />
          <button className="btn btn-primary" onClick={dismiss}>Close</button>
        </div>
      </Shell>
    );
  }

  /* ── Step 1: what each column is ───────────────────────────────────────── */
  const Columns = () => (
    <>
      <div className="setup-offer">
        <span>
          <b>{name}</b> — {sheet.rows.length} rows, {sheet.headers.length} columns
          {sheet.source === 'xlsx'
            ? (sheet.sheetNames && sheet.sheetNames.length > 1 && sheet.sheetName ? `, sheet “${sheet.sheetName}”` : '')
            : sheet.delimiter !== ',' ? `, separated by "${sheet.delimiter === '\t' ? 'tab' : sheet.delimiter}"` : ''}.
          {sheet.ragged.length > 0 && ` ${sheet.ragged.length} rows were a different width and have been filled out.`}
        </span>
      </div>
      {/* A WORKBOOK CAN HOLD MORE THAN ONE SHEET — #368. The first tab is often a
          cover or notes, so which one holds the backlog has to be a choice, not
          a guess. Only shown when there is more than one to choose between. */}
      {sheet.source === 'xlsx' && sheet.sheetNames && sheet.sheetNames.length > 1 && (
        <label className="grp-row is-work import-row">
          <div className="work-main" style={{ minWidth: 0 }}>
            <div style={{ color: 'var(--t1)', fontWeight: 550 }}>Which sheet</div>
            <div className="hint" style={{ fontSize: 12 }}>
              This workbook has {sheet.sheetNames.length} sheets — choose the one with your backlog.
            </div>
          </div>
          <select className="input import-pick" value={sheet.sheetName}
            onChange={(e) => void switchSheet(e.target.value)}>
            {sheet.sheetNames.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      )}
      {!titleSet && (
        <p className="feedback-problem" role="alert">
          One column has to be the title — it is what each card is called.
        </p>
      )}
      <div className="grp-list is-contents" style={{ maxHeight: 'none' }}>
        {sheet.headers.map((h, i) => (
          <div key={i} className="grp-row is-work import-row" >
            <div className="work-main" style={{ minWidth: 0 }}>
              <div style={{ color: 'var(--t1)', fontWeight: 550 }}>{h}</div>
              <div className="hint" style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {sheet.rows[0][i] || <i>empty on the first row</i>}
              </div>
            </div>
            <select
              className="input import-pick" value={columns[i]}
              onChange={(e) => setColumns(columns.map((c, n) => (n === i ? (e.target.value as Destination) : c)))}
            >
              {DESTINATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        ))}
      </div>
    </>
  );

  /* ── Step 2: what each word means ──────────────────────────────────────── */
  const Word = ({ q }: { q: WordQ }) => (
    <div className="grp-row is-work import-row" >
      <div className="work-main" style={{ minWidth: 0 }}>
        <div style={{ color: 'var(--t1)', fontWeight: 550 }}>
          <span className="hint" style={{ fontWeight: 400 }}>{sheet.headers[q.column]} · </span>{q.value}
        </div>
        <div className="hint" style={{ fontSize: 12 }}>
          {q.rows} {q.rows === 1 ? 'row' : 'rows'} · <span className={q.sure === 'exact' ? '' : 'is-worth-a-look'}>{SURE_NOTE[q.sure]}</span>
        </div>
      </div>
      <select
        className="input import-pick"
        value={values[`${q.field}:${q.value}`] ?? q.guess}
        onChange={(e) => setValues({ ...values, [`${q.field}:${q.value}`]: e.target.value })}
      >
        <option value="">Choose…</option>
        {(q.field === 'kind' ? kindChoices : q.field === 'status' ? statusChoices : (CHOICES[q.field] ?? [])).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        <option value="skip">Leave these rows out</option>
      </select>
    </div>
  );

  const Words = () => {
    const [showExact, setShowExact] = useState(false);
    const total = words.asks.length + words.reading.length + words.exact.length;
    const blockedRows = built?.problems.find((p) => p.kind === 'blocked-unanswered')?.rows ?? 0;
    const untypedRows = built?.problems.find((p) => p.kind === 'untyped-unanswered')?.rows ?? 0;
    return (
      <>
        <div className="setup-offer">
          <span>
            <b>{words.exact.length} of {total} words</b> are words FlightDeck uses too.
            {words.reading.length > 0 && ` ${words.reading.length} are our reading of yours`}
            {words.asks.length > 0 && `, and ${words.asks.length} we cannot work out`}.
          </span>
        </div>

        {blockedRows > 0 && (
          <div className="grp-row is-work import-row" >
            <div className="work-main" style={{ minWidth: 0 }}>
              <div style={{ color: 'var(--t1)', fontWeight: 550 }}>“Blocked” is not a status here</div>
              <div className="hint" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                {blockedRows} rows. FlightDeck keeps blocked separately, so a blocked card still has a
                real status and the reason sits beside it. What were these before they got stuck?
              </div>
            </div>
            <select className="input import-pick" value={blockedStatus}
              onChange={(e) => setBlockedStatus(e.target.value as IssueStatus)}>
              <option value="">Choose…</option>
              {statusChoices.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        )}

        {/* NO TYPE ON THE ROW — the same shape of question as Blocked, and asked
            for the same reason: a board's types are its own, so there is no
            honest one to assume. Shown when a row has an empty Type cell or the
            file has no Type column at all. Answered from this board's types. */}
        {untypedRows > 0 && (
          <div className="grp-row is-work import-row" >
            <div className="work-main" style={{ minWidth: 0 }}>
              <div style={{ color: 'var(--t1)', fontWeight: 550 }}>
                {columns.includes('kind') ? 'Some rows have no type' : 'The file has no type column'}
              </div>
              <div className="hint" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                {untypedRows} {untypedRows === 1 ? 'row' : 'rows'}. Every card on {meta.name} has a
                type, and this board’s are yours to set — so which of them should these become?
              </div>
            </div>
            <select className="input import-pick" value={defaultKind}
              onChange={(e) => setDefaultKind(e.target.value as IssueKind)}>
              <option value="">Choose…</option>
              {kindChoices.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        )}

        {words.asks.length > 0 && <>
          <div className="grp-heading">Needs an answer · {words.asks.length}</div>
          <div className="grp-list is-contents" style={{ maxHeight: 'none' }}>
            {words.asks.map((q) => <Word key={q.field + q.value} q={q} />)}
          </div>
        </>}

        {words.reading.length > 0 && <>
          <div className="grp-heading">Worth checking · {words.reading.length}</div>
          <div className="grp-list is-contents" style={{ maxHeight: 'none' }}>
            {words.reading.map((q) => <Word key={q.field + q.value} q={q} />)}
          </div>
        </>}

        {words.numeric.map((n) => <Bands key={n.column} sheet={sheet} column={n.column} bands={bands} setBands={setBands} />)}

        {words.exact.length > 0 && <>
          <button className="btn btn-small" style={{ alignSelf: 'flex-start' }} onClick={() => setShowExact(!showExact)}>
            {showExact ? 'Hide' : 'Show'} the {words.exact.length} that need nothing
          </button>
          {showExact && (
            <div className="grp-list is-contents" style={{ maxHeight: 'none' }}>
              {words.exact.map((q) => <Word key={q.field + q.value} q={q} />)}
            </div>
          )}
        </>}
      </>
    );
  };

  /* ── Step 3: what is about to happen ───────────────────────────────────── */
  const Check = () => {
    const counted = (built?.drafts ?? []).reduce<Record<string, number>>((m, d) => ({ ...m, [d.status]: (m[d.status] ?? 0) + 1 }), {});
    const left = (built?.problems ?? []).filter((p) => p.kind === 'no-title' || p.kind === 'unmapped-value');
    return (
      <>
        <div className="setup-offer">
          <span><b>{built?.drafts.length ?? 0} cards</b> will be added to {meta.name}. Nothing on the board is changed or removed.</span>
        </div>

        {clashing.length > 0 && (
          <p className="feedback-problem" role="alert">
            {clashing.length === (built?.drafts.length ?? 0)
              ? `Every one of these ${clashing.length} numbers is already on ${meta.name} — it looks like this file has been imported here before.`
              : `${clashing.length} of these numbers ${clashing.length === 1 ? 'is' : 'are'} already on ${meta.name} (${clashing.slice(0, 4).join(', ')}${clashing.length > 4 ? '…' : ''}).`}
            {' '}Two cards cannot share one, so {clashing.length === 1 ? 'that row' : 'those rows'} would be refused.
            {choice === 'keep' && theirScheme
              ? ' Under “The numbers” below, choose to number them # (or match the board) and they will be given fresh ones, or import onto a different board.'
              : ' Import onto a different board.'}
          </p>
        )}

        <div className="grp-heading">Where they land</div>
        <div className="hint" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {statusChoices.filter(([v]) => counted[v]).map(([v, l]) => (
            <span key={v}><b style={{ color: 'var(--t1)' }}>{counted[v]}</b> {l}</span>
          ))}
        </div>

        {/*
          THE DATES — #423. The one genuinely fiddly part of a schedule: 03/04 is
          two different days on two sides of the Atlantic. Shown only when a date
          column was mapped; a sample from the file is read back in the chosen
          order so a transposed day and month is caught before 78 cards land with
          it, and the order is set for the whole file at once because a file
          writes its dates one way throughout.
        */}
        {sheet && (columns.includes('due') || columns.includes('start')) && (() => {
          const dateField: Field = columns.includes('due') ? 'due' : 'start';
          const col = columns.indexOf(dateField);
          const sampleRaw = valuesIn(sheet, col)[0]?.value ?? '';
          const parsed = sampleRaw ? parseImportDate(sampleRaw, dateOrder) : null;
          const ORDERS: [DateOrder, string][] = [['dmy', 'Day first'], ['mdy', 'Month first'], ['ymd', 'Year first']];
          return (
            <>
              <div className="grp-heading">The dates</div>
              <p className="hint" style={{ margin: '0 0 8px', lineHeight: 1.6 }}>
                {sampleRaw ? (
                  <>
                    Your <b>{FIELD_LABEL[dateField]}</b> column reads{' '}
                    <b style={{ color: 'var(--t1)' }}>{sampleRaw}</b> as{' '}
                    {parsed
                      ? <b style={{ color: 'var(--t1)' }}>{asDayMonth(parsed)}</b>
                      : <b style={{ color: 'var(--t1)' }}>not a date</b>}.
                    {' '}If the day and month are the wrong way round, say which comes first.
                  </>
                ) : (
                  <>Nothing in the {FIELD_LABEL[dateField]} column reads as a date.</>
                )}
              </p>
              <div className="seg" role="group" aria-label="How this file writes its dates">
                {ORDERS.map(([o, l]) => (
                  <button
                    key={o}
                    type="button"
                    className={`seg-btn ${dateOrder === o ? 'active' : ''}`}
                    aria-pressed={dateOrder === o}
                    onClick={() => setDateOrder(o)}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </>
          );
        })()}

        {built && (
          <>
            <div className="grp-heading">The numbers</div>
            {theirScheme ? (
              <>
                {/* The sheet brought its own numbers. Show them against the
                    board's so the person reconciles the two — a prefix that is
                    the same or slightly different when transferring from an old
                    board (Vinnie, 11 Sep) — rather than keeping silently. */}
                <p className="hint" style={{ margin: '0 0 8px', lineHeight: 1.6 }}>
                  Your sheet numbers with <b>{theirScheme.prefix}</b> (e.g. {refFor(theirScheme, 1)}).{' '}
                  This board {boardScheme
                    ? <>numbers with <b>{boardScheme.prefix}</b> (next {refFor(boardScheme, boardScheme.next)})</>
                    : <>has no prefix — plain <b>#</b></>}.
                  {differs && <b style={{ color: 'var(--t1)' }}> — different, please check.</b>}
                  <br />How should these be numbered?
                </p>
                <label className="grp-row is-work import-row is-tick" >
                  <input type="radio" name="import-numbering" className="grp-pick" checked={choice === 'keep'} onChange={() => setChoice('keep')} />
                  <div className="work-main" style={{ minWidth: 0 }}>
                    <div style={{ color: 'var(--t1)' }}>Keep the sheet's {theirScheme.prefix} numbers</div>
                    <div className="hint" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                      Cards keep their own {theirScheme.prefix} references, so anything already signed off against them still matches.
                    </div>
                  </div>
                </label>
                {differs && (
                  <label className="grp-row is-work import-row is-tick" >
                    <input type="radio" name="import-numbering" className="grp-pick" checked={choice === 'board'} onChange={() => setChoice('board')} />
                    <div className="work-main" style={{ minWidth: 0 }}>
                      <div style={{ color: 'var(--t1)' }}>Match this board's {boardScheme!.prefix} numbering</div>
                      <div className="hint" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                        Renumbered from <b>{refFor(boardScheme!, boardScheme!.next)}</b> so the board keeps one scheme. Their own references stay in the descriptions.
                      </div>
                    </div>
                  </label>
                )}
                <label className="grp-row is-work import-row is-tick" >
                  <input type="radio" name="import-numbering" className="grp-pick" checked={choice === 'house'} onChange={() => setChoice('house')} />
                  <div className="work-main" style={{ minWidth: 0 }}>
                    <div style={{ color: 'var(--t1)' }}>Number them # instead</div>
                    <div className="hint" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                      Plain house numbers from <b>{refFor(houseScheme, houseScheme.next)}</b>. Their own references stay in the descriptions, but anything signed off against them will no longer match by number.
                    </div>
                  </div>
                </label>
              </>
            ) : boardScheme ? (
              <>
                {/* Numberless sheet, board already on a scheme from an earlier
                    import. Ask rather than assume, board wins by default. */}
                <p className="hint" style={{ margin: '0 0 8px', lineHeight: 1.5 }}>
                  Your sheet has no numbers of its own, and this board already numbers with{' '}
                  <b>{boardScheme!.prefix}</b> (from an earlier import). How should these be numbered?
                </p>
                <label className="grp-row is-work import-row is-tick" >
                  <input type="radio" name="import-numbering" className="grp-pick" checked={choice === 'board'} onChange={() => setChoice('board')} />
                  <div className="work-main" style={{ minWidth: 0 }}>
                    <div style={{ color: 'var(--t1)' }}>Continue this board's {boardScheme!.prefix} numbering</div>
                    <div className="hint" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                      These carry on from <b>{refFor(boardScheme!, boardScheme!.next)}</b>, matching what is already on the board.
                    </div>
                  </div>
                </label>
                <label className="grp-row is-work import-row is-tick" >
                  <input type="radio" name="import-numbering" className="grp-pick" checked={choice === 'house'} onChange={() => setChoice('house')} />
                  <div className="work-main" style={{ minWidth: 0 }}>
                    <div style={{ color: 'var(--t1)' }}>Number them # instead</div>
                    <div className="hint" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                      Plain house numbers from <b>{refFor(houseScheme, houseScheme.next)}</b>. Any references in the sheet stay in the descriptions.
                    </div>
                  </div>
                </label>
              </>
            ) : (
              /* Plain # board, numberless sheet — no competing scheme to choose
                 between, so a confirmation rather than a choice (Vinnie chose
                 "# vs board prefix only", 11 Sep). */
              <p className="hint" style={{ margin: 0, lineHeight: 1.5 }}>
                Your sheet has no numbers of its own, so these will be numbered{' '}
                <b>{refFor(houseScheme, houseScheme.next)}</b> onward.
              </p>
            )}
            {choice === 'keep' && numbered && numbered.renumbered.length > 0 && (
              <p className="hint" style={{ margin: 0 }}>
                {numbered.renumbered.length} of them are used twice in your sheet, so the later one gets
                the next free number: {numbered.renumbered.map((r) => `${r.was} → ${r.now}`).join(', ')}.
              </p>
            )}
          </>
        )}

        {built && built.sprints.length > 0 && (
          <>
            <div className="grp-heading">Sprints</div>
            <p className="hint" style={{ margin: 0, lineHeight: 1.55 }}>
              Your file names {built.sprints.length} —{' '}
              {/* Their order in the file is the order they were first mentioned, which
                  is not an order anybody reads a version list in. numeric so 1.10.0
                  sorts after 1.9.0 rather than before it. */}
              {[...built.sprints].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).join(', ')} — but not when they ran,
              and a sprint here needs a start and an end. They are <b>not</b> created; each card keeps the
              one it was in, in its description. Plan them on the Sprints screen when you are ready.
            </p>
          </>
        )}

        {left.length > 0 && (
          <>
            <div className="grp-heading">Still open · {left.length}</div>
            <div className="grp-list is-contents" style={{ maxHeight: 'none' }}>
              {left.map((p) => (
                <div key={p.kind + p.what} className="grp-row is-work import-row is-wide" >
                  <div className="work-main hint" style={{ minWidth: 0 }}>
                    {p.kind === 'no-title'
                      ? `${p.rows} rows have nothing in the title column and will be left out.`
                      : `${p.rows} rows say "${p.what.split(':').slice(1).join(':')}" and nothing was chosen for it — they will fall to the backlog.`}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </>
    );
  };

  const canGo = step === 0 ? titleSet : step === 1 ? stillAsking === 0 : true;

  return (
    <Shell leaving={leaving} dismiss={busy ? undefined : leave} step={step}>
      {step === 0 && <Columns />}
      {step === 1 && <Words />}
      {step === 2 && <Check />}
      {problem && <p className="feedback-problem" role="alert">{problem}</p>}
      {busy && (
        <div>
          <div className="setup-bar" role="progressbar" aria-valuemin={0} aria-valuenow={landed}
               aria-valuemax={built?.drafts.length ?? 0} aria-label="Importing">
            <span style={{ width: `${Math.round((landed / Math.max(1, built?.drafts.length ?? 1)) * 100)}%` }} />
          </div>
          {/* "sent", not "on the board". The bar counts rows ATTEMPTED so that a
              refused one does not stall it — which means on a run where the
              database is refusing every row it would otherwise have counted 78
              cards onto a board that received none. What happened to them is
              said properly on the screen after this one. */}
          <div className="setup-count" style={{ marginTop: 6 }}>
            {landed} of {built?.drafts.length ?? 0} sent
          </div>
        </div>
      )}
      <div className="feedback-foot">
        <span className="hint">
          {step === 0 && !titleSet && 'Set one column as the title to carry on.'}
          {step === 1 && stillAsking > 0 && `${stillAsking} still to answer.`}
          {step === 2 && (clashing.length > 0
            ? 'Nothing can be imported while those numbers clash.'
            : 'This cannot be undone from here.')}
        </span>
        <button className="btn" onClick={() => (step === 0 ? leave() : setStep(step - 1))} disabled={busy}>
          {step === 0 ? 'Cancel' : 'Back'}
        </button>
        {step < 2 ? (
          <button className="btn btn-primary" onClick={() => setStep(step + 1)} disabled={!canGo}>Next</button>
        ) : (
          <button className="btn btn-primary" onClick={() => void run()} disabled={busy || clashing.length > 0}>
            {busy ? 'Importing…' : `Import ${built?.drafts.length ?? 0} cards`}
          </button>
        )}
      </div>
    </Shell>
  );
}

type WordQ = { field: Field; column: number; value: string; rows: number; guess: string; sure: Sureness };

/**
 * A NUMBER COLUMN IS NOT TWENTY QUESTIONS.
 *
 * TRINRE score difficulty 1 to 10 and use nine of those numbers. Asking "what
 * is a 4" nine times over is a form, not a mapping. Two numbers cut the range
 * into the three sizes FlightDeck has, and the counts underneath show what that
 * does to the file before anything is written.
 */
function Bands({ sheet, column, bands, setBands }: {
  sheet: Sheet; column: number;
  bands: { quickUpTo: number; longFrom: number };
  setBands: (b: { quickUpTo: number; longFrom: number }) => void;
}) {
  const nums = sheet.rows.map((r) => Number(r[column])).filter((n) => Number.isFinite(n));
  const top = Math.max(...nums);
  const count = (f: (n: number) => boolean) => nums.filter(f).length;
  const { quickUpTo: lo, longFrom: hi } = bands;
  return (
    <>
      <div className="grp-heading">{sheet.headers[column]} → Size · {new Set(nums).size} numbers</div>
      <div className="grp-list is-contents" style={{ maxHeight: 'none' }}>
        <div className="grp-row is-work import-row is-wide" >
          <div className="work-main" style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', color: 'var(--t1)' }}>
              <span>Quick is up to</span>
              <select className="input" style={{ width: 64 }} value={lo}
                onChange={(e) => { const n = Number(e.target.value); setBands({ quickUpTo: n, longFrom: Math.max(hi, n + 1) }); }}>
                {Array.from({ length: top }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>· Long starts at</span>
              <select className="input" style={{ width: 64 }} value={hi}
                onChange={(e) => { const n = Number(e.target.value); setBands({ quickUpTo: Math.min(lo, n - 1), longFrom: n }); }}>
                {Array.from({ length: top }, (_, i) => i + 2).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="hint" style={{ fontSize: 12.5, marginTop: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span><b style={{ color: 'var(--t1)' }}>{count((n) => n <= lo)}</b> rows · Quick</span>
              <span><b style={{ color: 'var(--t1)' }}>{count((n) => n > lo && n < hi)}</b> rows · A few days</span>
              <span><b style={{ color: 'var(--t1)' }}>{count((n) => n >= hi)}</b> rows · Long</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Shell({ leaving, dismiss, step, children }: {
  leaving: boolean;
  /* Undefined while the import is actually writing: there is no sensible answer
     to "close this" half way through 78 rows, so the cross is disabled rather
     than hidden — a control that vanishes reads as a bug. */
  dismiss?: () => void;
  step: number;
  children: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef);
  return (
    <div className={`overlay setup-overlay ${leaving ? 'is-leaving' : ''}`} role="dialog" aria-modal="true" aria-label="Import from a spreadsheet">
      <div ref={panelRef} tabIndex={-1} className={`feedback is-import ${leaving ? 'is-leaving' : ''}`}>
        <div className="feedback-top">
          <h2>Import from a spreadsheet</h2>
          <button className="modal-close" onClick={dismiss} disabled={!dismiss}
                  aria-label={dismiss ? 'Close' : 'Cannot close while importing'}>
            <Icon name="close" size={16} />
          </button>
        </div>
        {step >= 0 && (
          <div className="import-steps grp-heading">
            {STEPS.map((s, i) => (
              <span key={s} className={`import-step ${i === step ? 'is-now' : ''} ${i < step ? 'is-past' : ''}`}>
                {i + 1}. {s}
              </span>
            ))}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
